'use client';

import { Box, Divider, Pagination } from '@wishket/design-system';
import { useState } from 'react';

import ArticleItemWidget from '../ArticleItemWidget';
import { ArticleListSkeleton } from '../ArticleList';

import { useDeviceCheck, useUIOverlay } from '@/shared/hooks';
import { FilterChip } from '@/shared/Components';
import { useAuthorArticleQuery } from '@/entities/author';
import EmptyContent from '@/features/EmptyContent';

type OrderType = 'latest' | 'popular';

const FIRST_PAGE = 1;

interface AuthorArticleListProps {
  authorId: number;
  defaultOrder?: 'latest' | 'popular';
}

const AuthorArticleList = ({
  authorId,
  defaultOrder = 'latest',
}: AuthorArticleListProps) => {
  const { isDesktop } = useDeviceCheck();
  const [orderType, setOrderType] = useState<OrderType>(defaultOrder);
  const [page, setPage] = useState(1);
  const { handleOpen } = useUIOverlay();
  const { data, isLoading } = useAuthorArticleQuery({
    authorId,
    ordering: orderType,
    page,
  });

  const isEmpty = data?.articles.length === 0;

  const changeOrderType = (type: OrderType) => {
    setOrderType(type);
    setPage(FIRST_PAGE);
  };

  const changePage = (pageNumber: number) => {
    if (typeof window === 'undefined') return;
    setPage(pageNumber);
    window.scrollTo({ top: 0 });
  };

  if (isLoading) return <ArticleListSkeleton />;

  if (!data || isEmpty) return <EmptyContent title="콘텐츠가 없어요." />;

  return (
    <Box className="flex w-full flex-col gap-12 desktop:gap-[60px]">
      <Box className="flex w-full flex-col gap-8 desktop:gap-10">
        <Box className="flex gap-3">
          <FilterChip
            label="최신순"
            onClick={() => changeOrderType('latest')}
            isActive={orderType === 'latest'}
          />
          <FilterChip
            label="인기순"
            onClick={() => changeOrderType('popular')}
            isActive={orderType === 'popular'}
          />
        </Box>
        <Box className="flex flex-col gap-6 desktop:gap-10">
          {data?.articles.map((article, idx) => {
            const isLastArticle = idx === data?.articles.length - 1;

            return (
              <Box
                key={String(article.id)}
                className="flex flex-col gap-6 desktop:gap-10"
              >
                <ArticleItemWidget {...article} isVisibleCategory={true} />
                {!isLastArticle && <Divider />}
              </Box>
            );
          })}
        </Box>
        <Pagination
          pageCount={isDesktop ? 10 : 5}
          maxPage={data?.maxPage}
          page={page}
          onChange={changePage}
        />
      </Box>
    </Box>
  );
};

export default AuthorArticleList;
